import { UserAction } from "@brookbush/types";

import { SignUp, useAuth } from "@clerk/nextjs";
import { Dialog, DialogPanel } from "@headlessui/react";
import clsx from "clsx";
import { getCookie } from "cookies-next";
import { AnimatePresence, motion } from "framer-motion";
import { XIcon } from "lucide-react";
import { useRouter } from "next/compat/router";
import Link from "next/link";
import posthog from "posthog-js";
import { memo, useEffect, useRef } from "react";

import MembershipFeatures from "./MembershipFeatures";
import { useUserAction } from "~/hooks";
import {
	getSignupModalTrackingProperties,
	getSignupModalViewport,
} from "~/lib/signup-modal.lib";
import {
	resolveSignupModalDenseMobileVariant,
	SIGNUP_MODAL_DENSE_MOBILE_FLAG,
} from "~/lib/signup-modal-dense-experiment";

const SignUpModal = () => {
	const { userAction, resetUserAction, setUserAction } = useUserAction();
	const isOpen = userAction === UserAction.SIGN_UP;
	const { isLoaded } = useAuth();
	const router = useRouter();

	const path = router?.asPath.split("?")[0]!;
	const panelRef = useRef<HTMLDivElement>(null);
	const formRef = useRef<HTMLDivElement>(null);

	const denseVariant = resolveSignupModalDenseMobileVariant(
		posthog.getFeatureFlag(SIGNUP_MODAL_DENSE_MOBILE_FLAG),
	);
	const isMobile = getSignupModalViewport() === "mobile";
	const isDenseMobile = isMobile && denseVariant === "test";

	const getTrackingProperties = () =>
		getSignupModalTrackingProperties(getSignupModalViewport(), denseVariant);

	useEffect(() => {
		if (!isOpen || !isLoaded) return;

		posthog.capture("signup_modal:opened", {
			...getSignupModalTrackingProperties(
				getSignupModalViewport(),
				denseVariant,
			),
			cta_click: getCookie(`${UserAction.SIGN_UP}:cta-click`),
		});
	}, [isOpen, isLoaded, denseVariant]);

	useEffect(() => {
		if (!isOpen || !isLoaded) return;
		if (getSignupModalViewport() !== "mobile") return;

		const root = panelRef.current;
		const target = formRef.current;
		if (!root || !target) return;

		let captured = false;

		const observer = new IntersectionObserver(
			([entry]) => {
				if (captured || !entry?.isIntersecting) return;

				captured = true;
				posthog.capture("signup_modal:form_visible", {
					...getSignupModalTrackingProperties(
						getSignupModalViewport(),
						denseVariant,
					),
					cta_click: getCookie(`${UserAction.SIGN_UP}:cta-click`),
				});
			},
			{ root, threshold: 0.15 },
		);

		observer.observe(target);

		return () => observer.disconnect();
	}, [isOpen, isLoaded, denseVariant]);

	const closeSignupModal = (reason: "x" | "backdrop") => {
		posthog.capture("signup_modal:closed", {
			...getTrackingProperties(),
			reason,
			cta_click: getCookie(`${UserAction.SIGN_UP}:cta-click`),
		});
		resetUserAction();
	};

	return (
		<AnimatePresence mode="wait">
			{isOpen && isLoaded && (
				<Dialog
					onClose={() => closeSignupModal("backdrop")}
					open={isOpen && isLoaded}
					static
				>
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1, transition: { duration: 0.3 } }}
						exit={{ opacity: 0, transition: { duration: 0.2 } }}
						className="fixed inset-0 z-50 bg-black/40 dark:bg-black/50"
						aria-hidden="true"
						key="signup-backdrop"
					/>

					<div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-4">
						<DialogPanel
							as={motion.div}
							ref={panelRef}
							initial={{ opacity: 0, y: 10 }}
							animate={{ opacity: 1, y: 0, transition: { duration: 0.25 } }}
							exit={{ opacity: 0, y: 10, transition: { duration: 0.15 } }}
							key="signup-modal"
							className="relative flex max-h-[100dvh] phablet:max-h-[90vh] w-full max-w-4xl phablet:flex-row flex-col overflow-y-auto rounded-t-xl bg-white shadow-black/5 shadow-xl sm:rounded-xl dark:bg-slate-800 dark:shadow-black/30"
						>
							<button
								className={clsx(
									"absolute right-4 z-50 flex cursor-pointer rounded-full bg-slate-100 p-1 transition-all hover:bg-slate-200 dark:phablet:bg-slate-700 dark:hover:phablet:bg-slate-600",
									isDenseMobile ? "top-2.5" : "top-3.5",
								)}
								onClick={() => closeSignupModal("x")}
							>
								<XIcon className="size-4.5 text-slate-700 dark:phablet:text-white" />
							</button>

							<div
								className={clsx(
									"h-auto phablet:w-1/2 phablet:border-r border-b phablet:border-b-0",
									isDenseMobile && "order-2",
								)}
							>
								<MembershipFeatures
									density={isDenseMobile ? "compact" : "default"}
								/>
							</div>

							<div
								ref={formRef}
								className={clsx(
									"flex phablet:w-1/2 w-full flex-col items-center justify-center",
									isDenseMobile
										? "order-1 gap-3 px-3 pt-3 pb-4"
										: "gap-6 phablet:gap-7 phablet:px-8 px-4 phablet:py-6 pt-4 pb-6",
								)}
							>
								<SignUp
									routing="hash"
									forceRedirectUrl={path}
									unsafeMetadata={{
										ctaClick: getCookie(`${UserAction.SIGN_UP}:cta-click`),
										signUpPath: path,
										isTrialUser:
											!!getCookie("is-trial-user") ||
											!!getCookie("referrer-id"),
										ipAddress: getCookie("ip-address"),
										userAgent: getCookie("user-agent"),
										auditedCourseId: getCookie("audited-course-id"),
										auditedVideoId: getCookie("audited-video-id"),
										auditedGlossaryTermId: getCookie(
											"audited-glossary-term-id",
										),
										referrerId: getCookie("referrer-id"),
										shouldReceiveGiftCard: getCookie("utm-gift-card"),
										posthogSessionId: posthog.get_session_id(),
									}}
								/>

								<div
									className={clsx(
										"flex flex-col",
										isDenseMobile ? "gap-3" : "gap-6",
									)}
								>
									<p className="text-center">
										Already have an account?{" "}
										<button
											onClick={() => setUserAction(UserAction.SIGN_IN)}
											className="text-sky-600 hover:underline dark:text-sky-300"
										>
											Sign In
										</button>
									</p>

									<p className="flex gap-6 text-sm">
										<Link
											href="/terms-of-service"
											target="_blank"
											className="font-medium hover:underline"
										>
											Terms of Service
										</Link>{" "}
										<Link
											href="/privacy-policy"
											target="_blank"
											className="font-medium hover:underline"
										>
											Privacy Policy
										</Link>
									</p>
								</div>
							</div>
						</DialogPanel>
					</div>
				</Dialog>
			)}
		</AnimatePresence>
	);
};

export default memo(SignUpModal);
